/**
 * The Core — one particle field that becomes each part of the story.
 *
 * Shapes (index → meaning):
 *   0 core      the company, at rest
 *   1 stack     software house: SaaS / CRM / CMS / ERP / databases
 *   2 flow      business automation: work moving node to node
 *   3 lab       in-house AI: a folded, thinking mass
 *   4 shell     private by design: core inside a cage
 *   5 burst     dawn: the field opens into light
 *
 * The page drives it with setState({ from, to, mix }); everything else is damped here.
 */

import * as THREE from "../js/vendor/three/three.module.js";
import { EffectComposer } from "./vendor/postprocessing/EffectComposer.js";
import { RenderPass } from "./vendor/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "./vendor/postprocessing/UnrealBloomPass.js";

export const SHAPES = { core: 0, stack: 1, flow: 2, lab: 3, shell: 4, burst: 5 };

const DARK = new THREE.Color("#0a0a0b");
const LIGHT = new THREE.Color("#f5f5f7");

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function onSphere(r, out) {
  // Marsaglia — uniform direction
  let x, y, s;
  do {
    x = r() * 2 - 1;
    y = r() * 2 - 1;
    s = x * x + y * y;
  } while (s >= 1 || s === 0);
  const k = 2 * Math.sqrt(1 - s);
  out[0] = x * k;
  out[1] = y * k;
  out[2] = 1 - 2 * s;
  return out;
}

function rotate(p, ax, ay) {
  // X then Y — bakes an isometric tilt into a shape
  const cx = Math.cos(ax), sx = Math.sin(ax);
  const cy = Math.cos(ay), sy = Math.sin(ay);
  const y1 = p[1] * cx - p[2] * sx;
  const z1 = p[1] * sx + p[2] * cx;
  const x2 = p[0] * cy + z1 * sy;
  const z2 = -p[0] * sy + z1 * cy;
  p[0] = x2;
  p[1] = y1;
  p[2] = z2;
  return p;
}

/* ------------------------------------------------------------------ shapes */

function shapeCore(n) {
  const r = rng(11);
  const out = new Float32Array(n * 3);
  const d = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    onSphere(r, d);
    const surface = r() < 0.74;
    const rad = surface ? 1.42 + (r() - 0.5) * 0.04 : 1.22 * Math.cbrt(r());
    out[i * 3] = d[0] * rad;
    out[i * 3 + 1] = d[1] * rad;
    out[i * 3 + 2] = d[2] * rad;
  }
  return out;
}

function shapeStack(n, portrait, layerOut) {
  const r = rng(23);
  const out = new Float32Array(n * 3);
  const w = portrait ? 2.1 : 2.8;
  const dpt = portrait ? 1.3 : 1.7;
  const gap = portrait ? 0.5 : 0.56;
  const p = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const layer = i % 5;
    layerOut[i] = layer;
    const y = (2 - layer) * gap;
    if (r() < 0.42) {
      // perimeter — crisp plate edges
      const per = 2 * (w + dpt);
      let t = r() * per;
      if (t < w) { p[0] = -w / 2 + t; p[2] = -dpt / 2; }
      else if ((t -= w) < dpt) { p[0] = w / 2; p[2] = -dpt / 2 + t; }
      else if ((t -= dpt) < w) { p[0] = w / 2 - t; p[2] = dpt / 2; }
      else { t -= w; p[0] = -w / 2; p[2] = dpt / 2 - t; }
    } else {
      // engineered fill — snapped to a fine grid
      const g = 0.1;
      p[0] = Math.round(((r() - 0.5) * w) / g) * g;
      p[2] = Math.round(((r() - 0.5) * dpt) / g) * g;
    }
    p[1] = y + (r() - 0.5) * 0.015;
    rotate(p, 0.52, -0.62);
    out[i * 3] = p[0];
    out[i * 3 + 1] = p[1];
    out[i * 3 + 2] = p[2];
  }
  return out;
}

/** One node per system in the example flow: Website → CRM → ERP → Inbox */
export const FLOW_NODES = 4;

function shapeFlow(n, portrait, edgeOut, nodeOut, nodesOut, centerOut) {
  const r = rng(37);
  const out = new Float32Array(n * 3);
  const NODES = FLOW_NODES;
  const nodes = nodesOut;
  nodes.length = 0;
  for (let k = 0; k < NODES; k++) {
    const t = k / (NODES - 1);
    const along = (t - 0.5) * (portrait ? 3.6 : 4.8);
    // a gentle zigzag, so the links read as one S-shaped path
    const sway = (k % 2 ? -1 : 1) * (portrait ? 0.42 : 0.34);
    const depth = (k % 2 ? -1 : 1) * 0.12;
    nodes.push(portrait ? [sway, -along, depth] : [along, sway, depth]);
  }
  const BEADS = 84; // dots per link
  const RING = 0.24;
  const OUTER = 0.36;
  const d = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    if (r() < 0.26) {
      // node: a solid core, a thin dial ring, and a faint outer ring that breathes when the node works
      const k = Math.floor(r() * NODES);
      const c = nodes[k];
      const kind = r();
      if (kind < 0.3) {
        onSphere(r, d);
        const rad = 0.075 * Math.cbrt(r());
        out[i * 3] = c[0] + d[0] * rad;
        out[i * 3 + 1] = c[1] + d[1] * rad;
        out[i * 3 + 2] = c[2] + d[2] * rad;
        nodeOut[i] = 1;
      } else {
        const outer = kind > 0.74;
        const a = r() * Math.PI * 2;
        const rad = (outer ? OUTER : RING) + (r() - 0.5) * (outer ? 0.02 : 0.012);
        out[i * 3] = c[0] + Math.cos(a) * rad;
        out[i * 3 + 1] = c[1] + Math.sin(a) * rad;
        out[i * 3 + 2] = c[2] + (r() - 0.5) * 0.01;
        nodeOut[i] = outer ? 3 : 2;
      }
      centerOut[i * 3] = c[0];
      centerOut[i * 3 + 1] = c[1];
      centerOut[i * 3 + 2] = c[2];
      edgeOut[i] = k / (NODES - 1);
    } else {
      // link: one hairline of evenly spaced beads along an S-curve, ring to ring
      const e = Math.floor(r() * (NODES - 1));
      const bead = Math.floor(r() * BEADS);
      const t = 0.07 + ((bead + 0.5) / BEADS) * 0.86 + (r() - 0.5) * 0.004;
      const a = nodes[e];
      const b = nodes[e + 1];
      const c1 = portrait ? [a[0], a[1] - 0.45, a[2]] : [a[0] + 0.45, a[1], a[2]];
      const c2 = portrait ? [b[0], b[1] + 0.45, b[2]] : [b[0] - 0.45, b[1], b[2]];
      const u = 1 - t;
      for (let j = 0; j < 3; j++) {
        out[i * 3 + j] =
          u * u * u * a[j] + 3 * u * u * t * c1[j] + 3 * u * t * t * c2[j] + t * t * t * b[j];
      }
      onSphere(r, d);
      const jitter = 0.011 * Math.cbrt(r());
      out[i * 3] += d[0] * jitter;
      out[i * 3 + 1] += d[1] * jitter;
      out[i * 3 + 2] += d[2] * jitter;
      edgeOut[i] = (e + t) / (NODES - 1);
      nodeOut[i] = 0;
      centerOut[i * 3] = 0;
      centerOut[i * 3 + 1] = 0;
      centerOut[i * 3 + 2] = 0;
    }
  }
  return out;
}

function shapeLab(n) {
  const r = rng(53);
  const out = new Float32Array(n * 3);
  const d = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const roll = r();
    if (roll < 0.7) {
      // folded cortex
      onSphere(r, d);
      const fold =
        Math.sin(d[0] * 7.0 + d[1] * 2.0) * Math.sin(d[1] * 6.0 - d[2] * 3.0) * Math.sin(d[2] * 8.0 + 1.0);
      const rad = 1.28 + fold * 0.16;
      out[i * 3] = d[0] * rad * 1.14;
      out[i * 3 + 1] = d[1] * rad * 0.94;
      out[i * 3 + 2] = d[2] * rad;
    } else if (roll < 0.9) {
      // inner neurons, loosely clustered
      onSphere(r, d);
      const rad = 0.95 * Math.pow(r(), 0.6);
      out[i * 3] = d[0] * rad;
      out[i * 3 + 1] = d[1] * rad;
      out[i * 3 + 2] = d[2] * rad;
    } else {
      // two orbit rings — the model in motion
      const a = r() * Math.PI * 2;
      const ring = r() < 0.5;
      const rad = 1.95 + (r() - 0.5) * 0.03;
      const p = [Math.cos(a) * rad, 0, Math.sin(a) * rad];
      rotate(p, ring ? 1.15 : -0.35, ring ? 0.4 : -0.9);
      out[i * 3] = p[0];
      out[i * 3 + 1] = p[1];
      out[i * 3 + 2] = p[2];
    }
  }
  return out;
}

function shapeShell(n, cageOut) {
  const r = rng(71);
  const out = new Float32Array(n * 3);
  const t = (1 + Math.sqrt(5)) / 2;
  const v = [
    [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
    [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
    [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
  ].map((p) => {
    const l = Math.hypot(p[0], p[1], p[2]);
    return p.map((c) => (c / l) * 1.8);
  });
  const edges = [];
  for (let a = 0; a < v.length; a++) {
    for (let b = a + 1; b < v.length; b++) {
      const dist = Math.hypot(v[a][0] - v[b][0], v[a][1] - v[b][1], v[a][2] - v[b][2]);
      if (dist < 1.95) edges.push([a, b]);
    }
  }
  const d = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    if (r() < 0.34) {
      onSphere(r, d);
      const rad = r() < 0.7 ? 0.56 : 0.5 * Math.cbrt(r());
      out[i * 3] = d[0] * rad;
      out[i * 3 + 1] = d[1] * rad;
      out[i * 3 + 2] = d[2] * rad;
      cageOut[i] = 0;
    } else {
      const [a, b] = edges[Math.floor(r() * edges.length)];
      const s = r();
      for (let j = 0; j < 3; j++) {
        out[i * 3 + j] = v[a][j] + (v[b][j] - v[a][j]) * s + (r() - 0.5) * 0.025;
      }
      cageOut[i] = 1;
    }
  }
  return out;
}

function shapeBurst(n) {
  const r = rng(89);
  const out = new Float32Array(n * 3);
  const d = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    // a wide, shallow bloom outward — never toward the lens
    onSphere(r, d);
    const rad = 4.5 + r() * 6;
    out[i * 3] = d[0] * rad;
    out[i * 3 + 1] = d[1] * rad * 0.8;
    out[i * 3 + 2] = Math.min(2, d[2] * rad * 0.35) - 2.5;
  }
  return out;
}

/* ------------------------------------------------------------------ shader */

const vertexShader = /* glsl */ `
uniform float uTime;
uniform float uMix;
uniform int uFrom;
uniform int uTo;
uniform float uSize;
uniform float uPixelRatio;
uniform float uFade;
uniform float uHighlight;
uniform float uHlAmt;
uniform float uContain;
uniform vec3 uPointer;
uniform float uPointerAmt;
uniform float uFlowHead;
uniform float uActiveNode;
uniform float uThink;
uniform float uVel;
uniform float uGain;

attribute vec3 aP0;
attribute vec3 aP1;
attribute vec3 aP2;
attribute vec3 aP3;
attribute vec3 aP4;
attribute vec3 aP5;
attribute float aRand;
attribute float aLayer;
attribute float aEdge;
attribute float aCage;
attribute float aNode;
attribute vec3 aNodeCenter;

varying float vAlpha;
varying float vGlow;

// The automation form: dial rings turn, and the outer ring of the node that is
// working breathes outward. Links and cores keep their place.
vec3 flowShaped() {
  if (aNode < 1.5) return aP2;
  float dir = aNode < 2.5 ? 1.0 : -0.55;
  float a = uTime * 0.22 * dir;
  vec3 rel = aP2 - aNodeCenter;
  float c = cos(a);
  float s = sin(a);
  rel.xy = vec2(rel.x * c - rel.y * s, rel.x * s + rel.y * c);
  float working = step(0.0, uActiveNode) * (1.0 - step(0.05, abs(aEdge - uActiveNode / ${(FLOW_NODES - 1).toFixed(1)})));
  float breath = fract(uTime * 0.6);
  rel *= 1.0 + step(2.5, aNode) * working * breath * 0.7;
  return aNodeCenter + rel;
}

vec3 shapeAt(int i) {
  if (i == 0) return aP0;
  if (i == 1) return aP1;
  if (i == 2) return flowShaped();
  if (i == 3) return aP3;
  if (i == 4) return aP4 * (aCage > 0.5 ? mix(1.0, 0.66, uContain) : 1.0);
  return aP5;
}

float presence(int i, float t) {
  return (uFrom == i ? 1.0 - t : 0.0) + (uTo == i ? t : 0.0);
}

void main() {
  // staggered, eased morph — every particle leaves at its own moment
  float local = clamp((uMix - aRand * 0.32) / 0.68, 0.0, 1.0);
  float t = local * local * (3.0 - 2.0 * local);

  vec3 p = mix(shapeAt(uFrom), shapeAt(uTo), t);

  // turbulence while travelling, stillness when arrived
  float travel = sin(t * 3.14159);
  p += travel * 0.38 * vec3(
    sin(aRand * 41.0 + uTime * 0.7),
    cos(aRand * 29.0 + uTime * 0.6),
    sin(aRand * 23.0 - uTime * 0.5)
  );

  // breathing
  p *= 1.0 + 0.014 * sin(uTime * 0.8 + aRand * 6.2831);

  float stack = presence(1, t);
  float flow = presence(2, t);
  float lab = presence(3, t);
  float shell = presence(4, t);

  // stack: the highlighted layer slides out like a drawer, toward the copy
  float match = 1.0 - step(0.1, abs(aLayer - uHighlight));
  float hl = stack * uHlAmt * match;
  float quiet = 1.0 - stack * uHlAmt * (1.0 - match) * 0.62;
  p.x -= hl * 0.62;

  // flow: beads of work moving along the links, the packet that travels in step
  // with the example flow beside it, and the node that is working
  float isNode = step(0.5, aNode);
  float working = step(0.0, uActiveNode) * (1.0 - step(0.05, abs(aEdge - uActiveNode / ${(FLOW_NODES - 1).toFixed(1)})));
  float beads = pow(max(0.0, sin((aEdge * 18.0 - uTime * 0.5) * 6.2831)), 60.0) * (1.0 - isNode);
  float headOn = step(0.0, uFlowHead);
  float head = headOn * exp(-pow((aEdge - uFlowHead) * 40.0, 2.0)) * (1.0 - isNode);
  float coreLit = working * step(0.5, aNode) * (1.0 - step(1.5, aNode));
  float ringLit = working * step(1.5, aNode) * (1.0 - step(2.5, aNode));
  float breath = fract(uTime * 0.6);
  float pulse = beads * 0.7 + head * 1.3 + coreLit * (0.35 + 0.15 * sin(uTime * 5.0)) + ringLit * 0.15;
  // the outer ring sits faint, and fades as it breathes out
  float outerAlpha = mix(0.3, 1.0 - breath, working);
  float flowAlpha = mix(0.26, 0.5, isNode) * mix(1.0, outerAlpha, step(2.5, aNode));

  // lab: synapse flicker — busier while the model is answering
  float rate = mix(0.992, 0.955, uThink);
  float flick = step(rate, fract(sin(aRand * 91.7 + floor(uTime * (4.0 + uThink * 8.0) + aRand * 13.0)) * 43758.5453));
  p *= 1.0 + lab * uThink * 0.02 * sin(uTime * 9.0 + aRand * 20.0);

  // shell: cage brightens when closed
  float cage = shell * aCage * uContain;

  vec4 world = modelMatrix * vec4(p, 1.0);

  // pointer: particles part around the cursor
  vec2 dir = world.xy - uPointer.xy;
  float dist = length(dir);
  world.xy += normalize(dir + 0.0001) * smoothstep(0.85, 0.0, dist) * 0.32 * uPointerAmt;

  // scroll speed: the field trails behind the page, like a liquid
  world.y -= uVel * (0.15 + aRand * aRand * 0.85) * 0.55;

  vec4 mv = viewMatrix * world;
  gl_Position = projectionMatrix * mv;

  float glow = hl * 1.6 + flow * pulse * 1.5 + lab * flick * 3.0 + cage * 0.6;
  vGlow = glow;
  // depth cue: the far side of a form recedes
  float depth = mix(0.35, 1.0, clamp((mv.z + 9.2) / 3.4, 0.0, 1.0));
  vAlpha = (0.16 + 0.42 * aRand) * depth * uFade * quiet * mix(1.0, flowAlpha, flow) * uGain;
  gl_PointSize = min(uSize * (0.55 + aRand * 0.9) * (1.0 + glow * 0.6) * mix(1.0, 0.6, flow * (1.0 - isNode)) / -mv.z, 9.0) * uPixelRatio;
}
`;

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
varying float vAlpha;
varying float vGlow;

void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.06, d);
  gl_FragColor = vec4(uColor * (1.0 + vGlow * 0.5), a * vAlpha * (1.0 + vGlow * 0.4));
}
`;

/* ------------------------------------------------------------------ core */

function damp(current, target, lambda, dt) {
  return current + (target - current) * (1 - Math.exp(-lambda * dt));
}

export function createCore(canvas, { reduced = false, from = null } = {}) {
  const small = Math.min(window.innerWidth, window.innerHeight) < 700;
  const weak = (navigator.hardwareConcurrency || 4) <= 4;
  const count = reduced ? 9000 : small || weak ? 14000 : 26000;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: "high-performance" });
  } catch {
    return null;
  }
  const dprCap = small ? 1.5 : 1.75;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, dprCap));
  // Hex values in, same values out — identical with or without the bloom composer
  THREE.ColorManagement.enabled = false;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setClearColor(DARK, 1);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 60);
  camera.position.set(0, 0, 7.2);

  const group = new THREE.Group();
  scene.add(group);

  const geometry = new THREE.BufferGeometry();
  const layer = new Float32Array(count);
  const edge = new Float32Array(count);
  const cageA = new Float32Array(count);
  const nodeA = new Float32Array(count);
  const centerA = new Float32Array(count * 3);
  const flowNodes = [];
  const rand = new Float32Array(count);
  const rr = rng(5);
  for (let i = 0; i < count; i++) rand[i] = rr();

  let portrait = window.innerWidth / window.innerHeight < 0.85;

  const core = shapeCore(count);
  geometry.setAttribute("position", new THREE.BufferAttribute(core, 3));
  geometry.setAttribute("aP0", new THREE.BufferAttribute(core, 3));
  geometry.setAttribute("aP1", new THREE.BufferAttribute(shapeStack(count, portrait, layer), 3));
  geometry.setAttribute("aP2", new THREE.BufferAttribute(shapeFlow(count, portrait, edge, nodeA, flowNodes, centerA), 3));
  geometry.setAttribute("aP3", new THREE.BufferAttribute(shapeLab(count), 3));
  geometry.setAttribute("aP4", new THREE.BufferAttribute(shapeShell(count, cageA), 3));
  geometry.setAttribute("aP5", new THREE.BufferAttribute(shapeBurst(count), 3));
  geometry.setAttribute("aRand", new THREE.BufferAttribute(rand, 1));
  geometry.setAttribute("aLayer", new THREE.BufferAttribute(layer, 1));
  geometry.setAttribute("aEdge", new THREE.BufferAttribute(edge, 1));
  geometry.setAttribute("aCage", new THREE.BufferAttribute(cageA, 1));
  geometry.setAttribute("aNode", new THREE.BufferAttribute(nodeA, 1));
  geometry.setAttribute("aNodeCenter", new THREE.BufferAttribute(centerA, 3));
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 14);

  const uniforms = {
    uTime: { value: 0 },
    uMix: { value: 0 },
    uFrom: { value: SHAPES.burst },
    uTo: { value: SHAPES.core },
    uSize: { value: small ? 26 : 28 },
    uPixelRatio: { value: renderer.getPixelRatio() },
    uFade: { value: 1 },
    uHighlight: { value: -1 },
    uHlAmt: { value: 0 },
    uContain: { value: 0 },
    uPointer: { value: new THREE.Vector3(99, 99, 0) },
    uPointerAmt: { value: 0 },
    uFlowHead: { value: -1 },
    uActiveNode: { value: -1 },
    uThink: { value: 0 },
    uVel: { value: 0 },
    // without bloom (phones), points carry a little more light themselves
    uGain: { value: small || reduced ? 1.45 : 1 },
    uColor: { value: new THREE.Color("#ecebe6") },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  group.add(new THREE.Points(geometry, material));

  let composer = null;
  if (!small && !reduced) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new UnrealBloomPass(new THREE.Vector2(256, 256), 0.42, 0.35, 0.22));
  }

  /* -------- state the page writes; damped values the GPU reads */
  const target = {
    from: SHAPES.core, to: SHAPES.core, mix: 0,
    x: 0, y: 0, s: 1,
    highlight: -1, contain: 0, dawn: 0,
    px: 0, py: 0, pointerOn: 0,
    think: 0, vel: 0,
  };
  const live = { x: 0, y: 0, s: 1, hlAmt: 0, contain: 0, dawn: 0, px: 0, py: 0, pointerOn: 0, think: 0, vel: 0 };
  let idleFrames = 0;
  // On load the field assembles from a burst — or, arriving from another page,
  // from the form that page was showing, so the core reads as one object.
  const introFrom = Number.isInteger(from) && from >= 0 && from <= 5 ? from : SHAPES.burst;
  const introLen = introFrom === SHAPES.burst ? 2.4 : 1.5;
  let intro = reduced ? 1 : 0;
  let preview = null;
  let previewFrom = SHAPES.core;
  let previewMix = 0;
  let spin = 0;

  function rebuildPortrait() {
    const next = window.innerWidth / window.innerHeight < 0.85;
    if (next === portrait) return;
    portrait = next;
    geometry.getAttribute("aP1").array.set(shapeStack(count, portrait, layer));
    geometry.getAttribute("aP2").array.set(shapeFlow(count, portrait, edge, nodeA, flowNodes, centerA));
    geometry.getAttribute("aP1").needsUpdate = true;
    geometry.getAttribute("aP2").needsUpdate = true;
    geometry.getAttribute("aLayer").needsUpdate = true;
    geometry.getAttribute("aEdge").needsUpdate = true;
    geometry.getAttribute("aNode").needsUpdate = true;
    geometry.getAttribute("aNodeCenter").needsUpdate = true;
  }

  // Screen anchors — where DOM labels should sit next to a form
  const tmp = new THREE.Vector3();
  function toScreen(local) {
    tmp.set(local[0], local[1], local[2]).applyMatrix4(group.matrixWorld).project(camera);
    return {
      x: (tmp.x * 0.5 + 0.5) * window.innerWidth,
      y: (-tmp.y * 0.5 + 0.5) * window.innerHeight,
    };
  }
  function stackAnchor(layerIndex) {
    const w = portrait ? 2.1 : 2.8;
    const gap = portrait ? 0.5 : 0.56;
    return toScreen(rotate([w / 2 + 0.08, (2 - layerIndex) * gap, 0], 0.52, -0.62));
  }
  function flowAnchor(k) {
    const n = flowNodes[k];
    if (!n) return { x: -999, y: -999 };
    // landscape: under the node; portrait (vertical pipeline): to its right
    return toScreen(portrait ? [n[0] + 0.48, n[1], n[2]] : [n[0], n[1] - 0.42, n[2]]);
  }

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    composer?.setSize(w, h);
    camera.aspect = w / h;
    // keep the shapes the same visual size on tall screens
    camera.position.z = w / h < 0.85 ? 9.4 : 7.2;
    camera.updateProjectionMatrix();
    rebuildPortrait();
  }
  resize();
  window.addEventListener("resize", resize);

  const clock = new THREE.Clock();
  const ray = new THREE.Vector3();
  let running = true;
  let raf = 0;

  function frame() {
    raf = requestAnimationFrame(frame);
    if (!running) return;
    const dt = Math.min(clock.getDelta(), 0.05);
    const time = clock.elapsedTime;
    uniforms.uTime.value = time;

    if (intro < 1) intro = Math.min(1, intro + dt / introLen);

    live.x = damp(live.x, target.x, 3.2, dt);
    live.y = damp(live.y, target.y, 3.2, dt);
    live.s = damp(live.s, target.s, 3.2, dt);
    live.hlAmt = damp(live.hlAmt, target.highlight >= 0 ? 1 : 0, 6, dt);
    live.contain = damp(live.contain, target.contain, 3, dt);
    live.dawn = damp(live.dawn, target.dawn, 4, dt);
    live.px = damp(live.px, target.px, 8, dt);
    live.py = damp(live.py, target.py, 8, dt);
    live.pointerOn = damp(live.pointerOn, target.pointerOn, 4, dt);
    previewMix = damp(previewMix, preview === null ? 0 : 1, 3.4, dt);
    live.think = damp(live.think, target.think, 3, dt);
    live.vel = damp(live.vel, target.vel, 5, dt);
    uniforms.uThink.value = live.think;
    uniforms.uVel.value = reduced ? 0 : live.vel;

    // Fully light and still: nothing to draw until the page moves again
    if (live.dawn > 0.995 && target.dawn > 0.995 && Math.abs(live.vel) < 0.002) {
      if (++idleFrames > 2) return;
    } else {
      idleFrames = 0;
    }

    if (intro < 1) {
      uniforms.uFrom.value = introFrom;
      uniforms.uTo.value = target.mix < 0.5 ? target.from : target.to;
      uniforms.uMix.value = 1 - Math.pow(1 - intro, 3);
    } else if (preview !== null || previewMix > 0.002) {
      uniforms.uFrom.value = previewFrom;
      uniforms.uTo.value = preview ?? uniforms.uTo.value;
      uniforms.uMix.value = previewMix;
    } else {
      uniforms.uFrom.value = target.from;
      uniforms.uTo.value = target.to;
      uniforms.uMix.value = target.mix;
    }

    if (target.highlight >= 0) uniforms.uHighlight.value = target.highlight;
    uniforms.uHlAmt.value = live.hlAmt;
    uniforms.uContain.value = live.contain;
    uniforms.uFade.value = 1 - live.dawn * 0.96;

    // rotation: the round forms turn, the diagrams hold still to be read
    const from = uniforms.uFrom.value;
    const to = uniforms.uTo.value;
    const m = uniforms.uMix.value;
    const turns = (i) => (i === SHAPES.core || i === SHAPES.lab || i === SHAPES.shell || i === SHAPES.burst ? 1 : 0);
    const spinAmt = turns(from) * (1 - m) + turns(to) * m;
    spin += dt * 0.14 * spinAmt;
    const rest = Math.round(spin / (Math.PI * 2)) * Math.PI * 2;
    spin += (rest - spin) * (1 - spinAmt) * Math.min(1, dt * 2.2);

    group.rotation.y = spin + live.px * 0.22;
    group.rotation.x = -live.py * 0.14;
    group.position.set(live.x, live.y, 0);
    group.scale.setScalar(live.s);
    group.updateMatrixWorld();

    // pointer in world space at z = 0
    ray.set(live.px, live.py, 0.5).unproject(camera).sub(camera.position).normalize();
    const k = -camera.position.z / ray.z;
    uniforms.uPointer.value.copy(camera.position).addScaledVector(ray, k);
    uniforms.uPointerAmt.value = live.pointerOn;

    renderer.setClearColor(DARK.clone().lerp(LIGHT, live.dawn), 1);

    if (composer && live.dawn < 0.98) composer.render();
    else renderer.render(scene, camera);
  }
  frame();

  document.addEventListener("visibilitychange", () => {
    running = !document.hidden;
    if (running) clock.getDelta();
  });

  return {
    count,
    stackAnchor,
    flowAnchor,
    /** How much of a shape is currently on screen, 0…1 (ignores per-particle stagger) */
    presence(shape) {
      const m = uniforms.uMix.value;
      return (uniforms.uFrom.value === shape ? 1 - m : 0) + (uniforms.uTo.value === shape ? m : 0);
    },
    /** Example-flow sync: head 0…1 along the pipeline (or -1), lit node index (or -1) */
    setFlow({ head = -1, node = -1 } = {}) {
      uniforms.uFlowHead.value = head;
      uniforms.uActiveNode.value = node;
    },
    setThink(v) {
      target.think = v;
    },
    /** Scroll speed, roughly −1…1 */
    setVelocity(v) {
      target.vel = v;
    },
    /** Visible half-extents of the z = 0 plane, for placing forms beside the copy */
    extent() {
      const halfH = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.position.z;
      return { halfW: halfH * camera.aspect, halfH };
    },
    setState({ from, to, mix }) {
      target.from = from;
      target.to = to;
      target.mix = mix;
    },
    setStage({ x, y, s }) {
      target.x = x;
      target.y = y;
      target.s = s;
    },
    preview(shape) {
      if (shape === preview) return;
      if (shape !== null && preview === null && previewMix > 0.002 && shape === uniforms.uTo.value) {
        // re-entering the same preview while it eases out: carry on from here
        preview = shape;
        return;
      }
      if (shape !== null && preview === null) {
        previewFrom = target.mix < 0.5 ? target.from : target.to;
        previewMix = 0;
      }
      if (shape !== null && preview !== null) {
        // hop straight from one preview to the next
        previewFrom = preview;
        previewMix = 0;
      }
      if (shape === null && preview !== null) {
        // ease back to wherever the page is
        previewFrom = target.mix < 0.5 ? target.from : target.to;
        const back = preview;
        preview = null;
        uniforms.uTo.value = back;
        return;
      }
      preview = shape;
    },
    setHighlight(i) {
      target.highlight = i ?? -1;
    },
    setContain(v) {
      target.contain = v;
    },
    setDawn(v) {
      target.dawn = v;
    },
    setPointer(nx, ny, on = 1) {
      target.px = nx;
      target.py = ny;
      target.pointerOn = on;
    },
    destroy() {
      cancelAnimationFrame(raf);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
}
